import type { InvestigationStep } from '@/types/investigation'

// The worker writes a one-line summary on every step (apps/functions/.../investigate-ticket/summary.ts).
// Runs from before it did get this smaller version of the same rules, so their cards read
// "Account …cb7e24: 21 consumptions" rather than the "{" their JSON starts with.

const MAX_CHARS = 160
const PRESENTATION_ARGS = new Set(['select', 'sort', 'limit', 'skip', 'page', 'populate', 'lookup'])
const LABEL_FIELDS = ['name', 'siteName', 'legalName', 'subject', 'title', 'fileName', 'description', 'ref']
const TEXT_ARGS = ['query', 'q', 'text', 'message', 'search', 'term']

const shortId = (id: string) => (/^[a-f\d]{24}$/i.test(id) ? `…${id.slice(-6)}` : id)
const cut = (text: string, max: number) => (text.length > max ? `${text.slice(0, max)}…` : text)

function argParts(args: unknown): string[] {
  if (!args || typeof args !== 'object') return []
  return Object.entries(args as Record<string, unknown>)
    .filter(([key, value]) => !PRESENTATION_ARGS.has(key) && value !== undefined && value !== null && value !== '')
    .map(([key, value]) => {
      const text = typeof value === 'string' ? value : JSON.stringify(value)
      if (key === 'id') return shortId(text)
      if (key.endsWith('Id') || key === 'ticket') return `${key.replace(/Id$/, '')} ${shortId(text)}`
      if (TEXT_ARGS.includes(key)) return `"${cut(text, 40)}"`
      return `${key} ${cut(text, 30)}`
    })
    .slice(0, 3)
}

function noun(tool: string, count: number): string {
  const base = tool.replace(/^(search|get|global)_/, '').replace(/_/g, ' ')
  const one = base === 'knowledge base' ? 'article' : base.endsWith('ies') ? `${base.slice(0, -3)}y` : base.replace(/s$/, '')
  if (count === 1) return `1 ${one}`
  return `${count} ${one.endsWith('y') ? `${one.slice(0, -1)}ies` : `${one}s`}`
}

function field(raw: string, name: string): string | undefined {
  return new RegExp(`"${name}"\\s*:\\s*"([^"]*)"`).exec(raw)?.[1]
}

function resultPart(tool: string, raw: string): string {
  if (raw.startsWith('ERROR:')) {
    const body = raw.replace(/^ERROR:\s*/, '').replace(/^API Error:\s*/, '')
    return `Error: ${cut(field(body, 'message') ?? body.split('\n')[0], 90)}`
  }
  if (!/^\s*[[{]/.test(raw)) return cut(raw.trim().split('\n')[0] ?? '', 80) || 'No output'
  // Read from the text, not parsed: stored previews are often cut short and no longer parse
  const total = /"total"\s*:\s*(\d+)/.exec(raw)?.[1]
  if (total !== undefined && !tool.startsWith('get_')) return Number(total) ? noun(tool, Number(total)) : 'No results'
  if (/^\s*\[\s*\]/.test(raw) || /"data"\s*:\s*\[\s*\]/.test(raw)) return 'No results'
  if (field(raw, 'proposalId')) return 'Proposed, awaiting approval'
  const label = LABEL_FIELDS.map(name => field(raw, name)).find(value => value?.trim())
  const meter = field(raw, 'meterPointNumber')
  const type = field(raw, 'type')
  const meterLabel = meter ? `${type === 'electricity' ? 'MPAN' : type === 'gas' ? 'MPRN' : 'meter'} ${meter}` : undefined
  return [label?.trim(), meterLabel].filter(Boolean).join(', ') || 'Record found'
}

export function stepSummary(step: InvestigationStep): string {
  if (step.summary) return step.summary
  const raw = step.resultPreview ?? ''
  const result = resultPart(step.tool, raw)
  const proposal = step.tool.startsWith('propose_') && typeof (step.args as { summary?: unknown })?.summary === 'string'
  const lead = proposal
    ? [(step.args as { summary: string }).summary]
    : step.tool.startsWith('get_') && !raw.startsWith('ERROR:')
      ? []
      : argParts(step.args)
  const line = lead.length ? `${lead.join(', ')}: ${result}` : result
  const capitalised = line.charAt(0).toUpperCase() + line.slice(1)
  return capitalised.length > MAX_CHARS ? `${capitalised.slice(0, MAX_CHARS - 1)}…` : capitalised
}
